import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  VARIABLE_TYPES,
  VARIABLE_TYPE_LABELS,
  buildActionConfig,
  buildRegexFromSegments,
  parseSegments,
  serializeSegments,
  suggestActionType,
  type RuleAction,
  type RuleGuard,
  type Segment,
  type TelegramActionType,
  type TelegramRule,
  type UpsertRuleInput,
  type VariableType,
} from '@wave/shared';
import { Modal } from '../Modal';
import { ActionChainBuilder, type TemplateLiveValues } from './ActionChainBuilder';
import {
  useTelegramChats,
  useTelegramHandlers,
  useTelegramMessages,
} from '../../api/hooks';
import {
  useCreateTelegramRule,
  useDryRunRule,
  useTestRegex,
  useUpdateTelegramRule,
  type RuleDryRunMatch,
  type RuleDryRunResult,
  type RuleDryRunResultItem,
} from '../../api/mutations';
import {
  btnPrimary,
  btnSecondary,
  btnSecondarySm,
  cardL2,
  fieldLabelCls,
  inputCls,
  subSectionTitle,
  textareaCls,
} from '../../lib/styles';

/**
 * Визуальный конструктор Telegram-listener правил для не-программиста.
 *
 * UX в виде wizard'а из трёх явных шагов:
 *  1. ПРИМЕР  — берём текст реального сообщения (из лога чата или вручную)
 *  2. РАЗМЕТКА — оператор выделяет в нём кусочки, помечая «это сумма / валюта / ID»
 *  3. ДЕЙСТВИЕ — система предлагает action, превью результата, привязка чатов
 *
 * Сырой regex и actionConfig JSON спрятаны под «Расширенный режим».
 * Сегменты сохраняются в `actionConfig._segments` для повторного редактирования.
 */
export function TelegramRuleEditorModal({
  rule,
  onClose,
}: {
  rule: TelegramRule | null;
  onClose: () => void;
}) {
  const { data: handlers } = useTelegramHandlers();
  const { data: chats } = useTelegramChats();
  const createMut = useCreateTelegramRule();
  const updateMut = useUpdateTelegramRule();
  const testMut = useTestRegex();

  const [name, setName] = useState(rule?.name ?? '');
  const [description, setDescription] = useState(rule?.description ?? '');
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [priority, setPriority] = useState(rule?.priority ?? 100);
  const [chatIds, setChatIds] = useState<string[]>(rule?.chatIds ?? []);
  // v2: единая цепочка действий (chain). Загружается из rule.actions[];
  // если rule из legacy — fallback на [{type: rule.actionType, config: rule.actionConfig}].
  const [allActions, setAllActions] = useState<RuleAction[]>(() => {
    const fromV2 = (rule?.actions ?? []) as RuleAction[];
    if (fromV2.length > 0) return fromV2;
    if (rule?.actionType) {
      return [{ type: rule.actionType as TelegramActionType, config: rule.actionConfig ?? {} }];
    }
    return [];
  });
  // v2: список guards.
  const [guards, setGuards] = useState<RuleGuard[]>(rule?.guards ?? []);
  const [autoApply, setAutoApply] = useState(rule?.autoApply ?? false);

  const initialSegments = useMemo<Segment[]>(() => {
    const fromConfig = (rule?.actionConfig as { _segments?: string } | undefined)?._segments;
    if (fromConfig) {
      const parsed = parseSegments(fromConfig);
      if (parsed) return parsed;
    }
    return [];
  }, [rule]);
  const [segments, setSegments] = useState<Segment[]>(initialSegments);

  // Если действий ещё нет — авто-создание первого по segments (suggestActionType).
  // Это даёт оператору рабочий чейн «из коробки» сразу после разметки.
  const suggestedType = useMemo(() => suggestActionType(segments), [segments]);
  useEffect(() => {
    if (allActions.length === 0 && segments.some((s) => s.kind === 'placeholder')) {
      setAllActions([
        {
          type: suggestedType,
          config: buildActionConfig(segments, suggestedType),
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, suggestedType]);
  // Legacy state — используется в advanced mode и при сохранении legacy actionType+actionConfig.
  const [actionType, setActionType] = useState<TelegramActionType>(
    (rule?.actionType as TelegramActionType) ?? suggestedType,
  );
  useEffect(() => {
    setActionType(allActions[0]?.type ?? suggestedType);
  }, [allActions, suggestedType]);

  const [advanced, setAdvanced] = useState(!!rule && initialSegments.length === 0);
  const [pattern, setPattern] = useState(rule?.pattern ?? '');
  const [flags, setFlags] = useState(rule?.flags ?? 'im');
  // Visual-режим: «все совпадения» = флаг g. При открытии существующего
  // правила восстанавливаем состояние тумблера из его flags.
  const [matchAll, setMatchAll] = useState((rule?.flags ?? '').includes('g'));
  const [actionConfigJson, setActionConfigJson] = useState(() => {
    const cfg = rule?.actionConfig ?? {};
    const stripped: Record<string, unknown> = { ...(cfg as Record<string, unknown>) };
    delete stripped._segments;
    return JSON.stringify(stripped, null, 2);
  });

  const visualBuilt = useMemo(
    () => buildRegexFromSegments(segments, { matchAll }),
    [segments, matchAll],
  );
  useEffect(() => {
    if (advanced) return;
    setPattern(visualBuilt.pattern);
    setFlags(visualBuilt.flags);
  }, [visualBuilt, advanced]);

  const primaryChatId = chatIds[0] ?? null;
  const { data: messages } = useTelegramMessages(primaryChatId, 30);
  const [sample, setSample] = useState('');
  const [sampleTab, setSampleTab] = useState<'chat' | 'manual'>(
    primaryChatId ? 'chat' : 'manual',
  );

  // Live-captures клиентски (мгновенно, без запросов): аналог серверного
  // matchAllNamed (rule-engine.ts) — named groups всех матчей sample.
  // С флагом g — matchAll с капом 100 матчей, без — один exec. Кривой
  // advanced-паттерн не должен ронять рендер → try/catch, просто нет значений.
  const liveCaptures = useMemo(() => {
    if (!pattern || !sample) return null;
    try {
      const re = new RegExp(pattern, flags);
      const all: Array<Record<string, string | undefined>> = [];
      if (re.flags.includes('g')) {
        for (const m of sample.matchAll(re)) {
          all.push({ ...(m.groups ?? {}) });
          if (all.length >= 100) break; // кап как в серверном matchAllNamed
        }
      } else {
        const m = re.exec(sample);
        if (m) all.push({ ...(m.groups ?? {}) });
      }
      return all.length > 0 ? all : null;
    } catch {
      return null;
    }
  }, [pattern, flags, sample]);

  // Живые значения для чипов конструктора действий: capture-группы первого
  // матча + те системные плейсхолдеры, что известны без запросов.
  const templateLive = useMemo<TemplateLiveValues | null>(() => {
    const system: Record<string, string> = {};
    if (sample) system._message_text = sample;
    if (primaryChatId) {
      system._chat_id = primaryChatId;
      const title = (chats ?? []).find((c) => c.chatId === primaryChatId)?.chatTitle;
      if (title) system._chat_title = title;
    }
    if (!liveCaptures && Object.keys(system).length === 0) return null;
    return {
      groups: liveCaptures?.[0] ?? {},
      allGroups: liveCaptures ?? [],
      system,
    };
  }, [liveCaptures, sample, primaryChatId, chats]);

  const [testResult, setTestResult] = useState<{
    ok: boolean;
    error: string | null;
    matches: Array<{ match: string; groups: Record<string, string> }>;
  } | null>(null);
  useEffect(() => {
    if (!pattern || !sample) {
      setTestResult(null);
      return;
    }
    const handle = setTimeout(() => {
      testMut
        .mutateAsync({ pattern, flags, sample })
        .then(setTestResult)
        .catch(() => setTestResult(null));
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pattern, flags, sample]);

  const [error, setError] = useState<string | null>(null);

  // Step status для индикатора (визуально, не блокирующий)
  const step1Done = !!sample;
  const step2Done = segments.length > 0 && segments.some((s) => s.kind === 'placeholder');
  const step3Done = chatIds.length > 0 && !!name.trim();

  async function save() {
    setError(null);
    // v2: chain — единый источник истины (allActions). Legacy actionConfig
    // дублируем из первого action для backward-compat бэкенда.
    let actions: RuleAction[];
    if (advanced) {
      let parsedConfig: Record<string, unknown>;
      try {
        parsedConfig = actionConfigJson.trim() === '' ? {} : JSON.parse(actionConfigJson);
      } catch (err) {
        setError(`actionConfig: invalid JSON — ${(err as Error).message}`);
        return;
      }
      // Advanced-JSON редактируется без служебного _segments (он скрыт при
      // инициализации actionConfigJson) — подмешиваем сегменты обратно в config
      // первого action, иначе один проход через «Расширенный режим» навсегда
      // стирает визуальную разметку. Приоритет — актуальный state segments
      // (мог быть отредактирован в visual-режиме в этой же сессии), fallback —
      // сегменты из сохранённого правила.
      const preservedSegments = segments.some((s) => s.kind === 'placeholder')
        ? serializeSegments(segments)
        : (rule?.actionConfig as { _segments?: string } | undefined)?._segments;
      actions = [
        {
          type: actionType,
          config: preservedSegments
            ? { ...parsedConfig, _segments: preservedSegments }
            : parsedConfig,
        },
      ];
    } else {
      if (allActions.length === 0) {
        setError('Добавь хотя бы одно действие на шаге 3');
        return;
      }
      // В config первого action сохраняем сегменты — для повторного открытия в visual editor.
      const firstWithSegments = {
        ...allActions[0]!,
        config: { ...allActions[0]!.config, _segments: serializeSegments(segments) },
      };
      actions = [firstWithSegments, ...allActions.slice(1)];
    }
    const primary = actions[0]!;
    const input: UpsertRuleInput = {
      name: name.trim(),
      description: description.trim() || null,
      enabled,
      pattern,
      flags,
      actionType: primary.type,
      actionConfig: primary.config,
      actions,
      guards,
      autoApply,
      priority,
      chatIds,
    };
    try {
      if (rule) {
        await updateMut.mutateAsync({ id: rule.id, patch: input });
      } else {
        await createMut.mutateAsync(input);
      }
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const isPending = createMut.isPending || updateMut.isPending;
  const canSave = !!name.trim() && (advanced ? !!pattern : segments.length > 0);

  return (
    <Modal title={rule ? `Правило: ${rule.name}` : 'Создание правила'} onClose={onClose} size="lg">
      {/* Step indicator */}
      <Stepper
        steps={[
          { label: 'Пример', done: step1Done, active: !step1Done },
          {
            label: 'Разметка',
            done: step2Done,
            active: step1Done && !step2Done,
          },
          {
            label: 'Действие',
            done: step3Done,
            active: step2Done && !step3Done,
          },
        ]}
      />

      <div className="mt-5 space-y-4">
        {!advanced && (
          <>
            {/* ─── Шаг 1: Пример ───────────────────────────────────────── */}
            <Section number={1} title="Возьми пример реального сообщения" done={step1Done}>
              <div className="mb-2 inline-flex gap-1 rounded-md bg-bg3 p-0.5">
                <SampleTabButton
                  active={sampleTab === 'chat'}
                  onClick={() => setSampleTab('chat')}
                >
                  Из чата
                </SampleTabButton>
                <SampleTabButton
                  active={sampleTab === 'manual'}
                  onClick={() => setSampleTab('manual')}
                >
                  Свой текст
                </SampleTabButton>
              </div>

              {sampleTab === 'chat' && (
                <>
                  {!primaryChatId ? (
                    <EmptyHint>
                      Сначала выбери чат на шаге 3 — здесь появятся его последние сообщения, по
                      одному клику можно взять как пример.
                    </EmptyHint>
                  ) : !messages || messages.length === 0 ? (
                    <EmptyHint>
                      В этом чате пока нет сообщений. Переключись на «Свой текст» и введи пример
                      вручную.
                    </EmptyHint>
                  ) : (
                    <div className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-border bg-bg p-1.5">
                      {messages
                        .filter((m) => !!m.text)
                        .slice(0, 15)
                        .map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => setSample(m.text ?? '')}
                            className={`flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-bg2 ${
                              sample === m.text
                                ? 'bg-[var(--accent-bg)] text-text'
                                : 'text-text2 hover:text-text'
                            }`}
                          >
                            <span className="mt-0.5 font-mono text-[10px] text-text-subtle">
                              #{m.messageId}
                            </span>
                            <span className="flex-1 break-words">{m.text}</span>
                          </button>
                        ))}
                    </div>
                  )}
                </>
              )}

              {sampleTab === 'manual' && (
                <AutoGrowTextarea
                  value={sample}
                  onChange={setSample}
                  placeholder="Например: Получено 50000 RUB по заявке W-001"
                />
              )}

              {sample && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-[var(--accent-border)] bg-[var(--accent-bg)] p-2.5">
                  <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent">
                    Выбран:
                  </span>
                  <span className="flex-1 font-mono text-xs text-text">{sample}</span>
                  <button
                    type="button"
                    onClick={() => setSample('')}
                    className="text-text-subtle hover:text-text"
                    title="Сбросить"
                  >
                    ✕
                  </button>
                </div>
              )}
            </Section>

            {/* ─── Шаг 2: Разметка ─────────────────────────────────────── */}
            <Section
              number={2}
              title="Выдели в сообщении переменные"
              done={step2Done}
              disabled={!sample}
            >
              {!sample ? (
                <EmptyHint>Сначала выбери пример сообщения на шаге 1.</EmptyHint>
              ) : (
                <>
                  <SegmentBuilder sample={sample} segments={segments} onChange={setSegments} />
                  {testResult && segments.some((s) => s.kind === 'placeholder') && (
                    <div className="mt-3">
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-subtle">
                        Проверка на примере
                      </div>
                      <TestMatchesList testResult={testResult} />
                    </div>
                  )}
                  {segments.some((s) => s.kind === 'placeholder') && (
                    <div className="mt-3 border-t border-border-subtle pt-3">
                      <DryRunPanel
                        pattern={pattern}
                        flags={flags}
                        guards={guards}
                        getActions={() =>
                          allActions.length > 0
                            ? allActions
                            : { error: 'Добавь хотя бы одно действие на шаге 3' }
                        }
                        sample={sample}
                        chatId={primaryChatId}
                        handlers={handlers ?? []}
                      />
                    </div>
                  )}
                </>
              )}
            </Section>

            {/* ─── Шаг 3: Действия (chain) ─────────────────────────────── */}
            <Section
              number={3}
              title="Что делаем при совпадении"
              done={step3Done}
              disabled={!step2Done}
            >
              {!step2Done ? (
                <EmptyHint>Сначала размечь хотя бы одну переменную на шаге 2.</EmptyHint>
              ) : (
                <ActionChainBuilder
                  actions={allActions}
                  onChange={setAllActions}
                  handlers={handlers ?? []}
                  segments={segments}
                  chats={chats ?? []}
                  live={templateLive}
                />
              )}
            </Section>

            {/* ─── Шаг 4: Условия + чаты + название ─────────────────────── */}
            <Section number={4} title="Условия, чаты, название" done={step3Done}>
              <div className="space-y-3">
                <GuardsEditor guards={guards} onChange={setGuards} segments={segments} />
                <div className="border-t border-border-subtle pt-3">
                  <label className={fieldLabelCls}>В каких чатах работает</label>
                  <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border border-border bg-bg p-1.5">
                    {(chats ?? []).length === 0 ? (
                      <div className="px-2 py-1 text-[11px] text-text-subtle">
                        Сначала зарегистрируй чаты на вкладке «Чаты».
                      </div>
                    ) : (
                      (chats ?? []).map((c) => (
                        <label
                          key={c.chatId}
                          className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-text hover:bg-bg2"
                        >
                          <input
                            type="checkbox"
                            checked={chatIds.includes(c.chatId)}
                            onChange={(e) => {
                              if (e.target.checked) setChatIds([...chatIds, c.chatId]);
                              else setChatIds(chatIds.filter((id) => id !== c.chatId));
                            }}
                          />
                          <span>{c.chatTitle ?? c.chatId}</span>
                          <span className="font-mono text-[10px] text-text-subtle">{c.chatId}</span>
                          {!c.enabled && <span className="text-[10px] text-text-subtle">(выкл)</span>}
                        </label>
                      ))
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 border-t border-border-subtle pt-3 md:grid-cols-2">
                  <div>
                    <label className={fieldLabelCls}>Название правила</label>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={inputCls}
                      placeholder="Например: Тинькофф — поступление RUB"
                    />
                  </div>
                  <div>
                    <label className={fieldLabelCls}>Описание (необязательно)</label>
                    <input
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className={inputCls}
                      placeholder="Для чего это правило"
                    />
                  </div>
                </div>
              </div>
            </Section>
          </>
        )}

        {advanced && (
          <AdvancedFields
            pattern={pattern}
            setPattern={setPattern}
            flags={flags}
            setFlags={setFlags}
            actionConfigJson={actionConfigJson}
            setActionConfigJson={setActionConfigJson}
            sample={sample}
            setSample={setSample}
            testResult={testResult}
            handlerConfigSchema={handlers?.find((h) => h.type === actionType)?.configSchema ?? null}
            actionType={actionType}
            handlers={handlers ?? []}
            onChangeActionType={(t) => setActionType(t)}
            name={name}
            onChangeName={setName}
            chats={chats ?? []}
            chatIds={chatIds}
            onChangeChatIds={setChatIds}
            dryRunPanel={
              <DryRunPanel
                pattern={pattern}
                flags={flags}
                guards={guards}
                getActions={() => {
                  try {
                    const parsed =
                      actionConfigJson.trim() === ''
                        ? {}
                        : (JSON.parse(actionConfigJson) as Record<string, unknown>);
                    return [{ type: actionType, config: parsed }];
                  } catch (err) {
                    return { error: `actionConfig: invalid JSON — ${(err as Error).message}` };
                  }
                }}
                sample={sample}
                chatId={primaryChatId}
                handlers={handlers ?? []}
              />
            }
          />
        )}

        {/* ─── Footer ──────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle pt-3">
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <label className="flex items-center gap-1.5 text-text">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Включено
            </label>
            <label
              className="flex items-center gap-1.5 text-text"
              title="При совпадении сразу применить, без подтверждения оператором"
            >
              <input
                type="checkbox"
                checked={autoApply}
                onChange={(e) => setAutoApply(e.target.checked)}
              />
              Авто-применение
            </label>
            <label className="flex items-center gap-1.5 text-text2">
              <span>Приоритет</span>
              <input
                type="number"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className={`${inputCls} h-7 w-16 px-2 text-xs`}
                min={0}
                max={999}
              />
            </label>
            {!advanced && (
              <label
                className="flex items-center gap-1.5 text-text"
                title="Флаг g: правило применяется к каждой подошедшей строке сообщения — мультивалютный баланс из 5 строк даст 5 событий"
              >
                <input
                  type="checkbox"
                  checked={matchAll}
                  onChange={(e) => setMatchAll(e.target.checked)}
                />
                Все совпадения в сообщении (событие на каждую строку)
              </label>
            )}
            <label className="flex items-center gap-1.5 text-text2">
              <input
                type="checkbox"
                checked={advanced}
                onChange={(e) => {
                  // При возврате в visual-режим тумблер «все совпадения»
                  // подхватывает актуальные flags из advanced-редактора.
                  if (!e.target.checked) setMatchAll(flags.includes('g'));
                  setAdvanced(e.target.checked);
                }}
              />
              Расширенный режим
            </label>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className={btnSecondary}>
              Отмена
            </button>
            <button
              type="button"
              disabled={isPending || !canSave}
              onClick={() => void save()}
              className={btnPrimary}
            >
              {isPending ? 'Сохранение…' : rule ? 'Сохранить изменения' : 'Создать правило'}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Stepper indicator
// ════════════════════════════════════════════════════════════════════════════

function Stepper({
  steps,
}: {
  steps: { label: string; done: boolean; active: boolean }[];
}) {
  return (
    <div className="flex items-center gap-1">
      {steps.map((s, i) => (
        <div key={s.label} className="flex flex-1 items-center gap-2">
          <div
            className={`flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold ${
              s.done
                ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-success'
                : s.active
                  ? 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent'
                  : 'border-border bg-bg2 text-text-subtle'
            }`}
          >
            {s.done ? '✓' : i + 1}
          </div>
          <span
            className={`text-xs font-medium ${
              s.done ? 'text-success' : s.active ? 'text-accent' : 'text-text-subtle'
            }`}
          >
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <div
              className={`h-px flex-1 ${
                steps[i + 1]?.done || s.done ? 'bg-[var(--green-border)]' : 'bg-border'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Section card (одна карточка-шаг wizard'а)
// ════════════════════════════════════════════════════════════════════════════

function Section({
  number,
  title,
  done,
  disabled,
  children,
}: {
  number: number;
  title: string;
  done: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`${cardL2} ${disabled ? 'opacity-60' : ''}`}
    >
      <div className="mb-3 flex items-center gap-2">
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
            done
              ? 'bg-[var(--green-bg)] text-success'
              : 'bg-[var(--accent-bg)] text-accent'
          }`}
        >
          {done ? '✓' : number}
        </span>
        <h3 className={subSectionTitle}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function SampleTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
        active ? 'bg-bg text-text shadow-sm' : 'text-text2 hover:text-text'
      }`}
    >
      {children}
    </button>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-bg px-3 py-2.5 text-xs text-text-subtle">
      {children}
    </div>
  );
}

/**
 * Результат live-тестера: ВСЕ совпадения regex по sample нумерованным списком.
 * При флаге `g` бэкенд (POST /rules/test → matchAllNamed) возвращает каждое
 * совпадение отдельно — каждое из них станет отдельным событием. Без `g`
 * список из одного элемента, нумерация не мешает.
 */
function TestMatchesList({
  testResult,
}: {
  testResult: {
    ok: boolean;
    error: string | null;
    matches: Array<{ match: string; groups: Record<string, string> }>;
  };
}) {
  if (!testResult.ok) {
    return (
      <div className="rounded border border-[var(--red-border)] bg-[var(--red-bg)] px-2 py-1 text-[11px] text-danger">
        ✗ {testResult.error}
      </div>
    );
  }
  if (testResult.matches.length === 0) {
    return (
      <div className="rounded border border-dashed border-border bg-bg px-2 py-1 text-[11px] text-text2">
        Нет совпадений — проверь разметку и пример.
      </div>
    );
  }
  return (
    <div className="space-y-1 text-[11px]">
      <div className="text-text-subtle">
        Совпадений:{' '}
        <span className="font-semibold text-text">{testResult.matches.length}</span>
        {testResult.matches.length > 1 && ' — каждое породит отдельное событие'}
      </div>
      {testResult.matches.map((m, i) => (
        <div
          key={i}
          className="rounded border border-[var(--green-border)] bg-[var(--green-bg)] px-2 py-1 font-mono"
        >
          <div className="flex items-baseline gap-1.5">
            <span className="shrink-0 text-[10px] font-semibold text-success">#{i + 1}</span>
            <span className="break-all text-success">✓ {m.match}</span>
          </div>
          {Object.keys(m.groups).length > 0 && (
            <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-text2">
              {Object.entries(m.groups).map(([k, v]) => (
                <span key={k}>
                  <span className="text-text-subtle">{k}=</span>
                  <span className="text-text">{v}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Шаг 2: Размечание сегментов
// ════════════════════════════════════════════════════════════════════════════

/**
 * Цветовая семантика chip-ов и кнопок по группе типа:
 *  - money (green) — деньги
 *  - id (accent/yellow/blue) — идентификаторы
 *  - contact (info) — телефоны/email/имена/username
 *  - requisite (warning) — реквизиты карт/IBAN/счетов/SWIFT
 *  - time (info) — дата/время
 *  - crypto (violet) — крипто-адреса/хэши транзакций. CSS-vars фиолетовой
 *    палитры в :root нет — hex Tailwind violet-50/200 (bg/border идут в
 *    inline-style) + класс text-violet-600 (дефолтная Tailwind-палитра
 *    доступна: theme.extend не перекрывает colors).
 *  - other (gray) — URL/произвольные значения
 */
const TYPE_STYLE: Record<VariableType, { bg: string; border: string; text: string; emoji: string }> = {
  amount:        { bg: 'var(--green-bg)',  border: 'var(--green-border)',  text: 'text-success', emoji: '₽' },
  signedAmount:  { bg: 'var(--green-bg)',  border: 'var(--green-border)',  text: 'text-success', emoji: '±' },
  percent:       { bg: 'var(--green-bg)',  border: 'var(--green-border)',  text: 'text-success', emoji: '%' },
  ccy:           { bg: 'var(--accent-bg)', border: 'var(--accent-border)', text: 'text-accent',  emoji: '¤' },
  ccySymbol:     { bg: 'var(--accent-bg)', border: 'var(--accent-border)', text: 'text-accent',  emoji: '$' },
  orderId:       { bg: 'var(--yellow-bg)', border: 'var(--yellow-border)', text: 'text-warning', emoji: '#' },
  idx:           { bg: 'var(--blue-bg)',   border: 'var(--blue-border)',   text: 'text-info',    emoji: '№' },
  phone:         { bg: 'var(--blue-bg)',   border: 'var(--blue-border)',   text: 'text-info',    emoji: '☎' },
  email:         { bg: 'var(--blue-bg)',   border: 'var(--blue-border)',   text: 'text-info',    emoji: '@' },
  tgUsername:    { bg: 'var(--blue-bg)',   border: 'var(--blue-border)',   text: 'text-info',    emoji: '✈' },
  name:          { bg: 'var(--blue-bg)',   border: 'var(--blue-border)',   text: 'text-info',    emoji: '👤' },
  cardLast4:     { bg: 'var(--yellow-bg)', border: 'var(--yellow-border)', text: 'text-warning', emoji: '▣' },
  cardNumber:    { bg: 'var(--yellow-bg)', border: 'var(--yellow-border)', text: 'text-warning', emoji: '💳' },
  iban:          { bg: 'var(--yellow-bg)', border: 'var(--yellow-border)', text: 'text-warning', emoji: '🏦' },
  swiftBic:      { bg: 'var(--yellow-bg)', border: 'var(--yellow-border)', text: 'text-warning', emoji: '🏛' },
  accountNumber: { bg: 'var(--yellow-bg)', border: 'var(--yellow-border)', text: 'text-warning', emoji: '⌗' },
  cryptoAddress: { bg: '#f5f3ff',          border: '#ddd6fe',              text: 'text-violet-600', emoji: '₿' },
  txHash:        { bg: '#f5f3ff',          border: '#ddd6fe',              text: 'text-violet-600', emoji: '⛓' },
  date:          { bg: 'var(--blue-bg)',   border: 'var(--blue-border)',   text: 'text-info',    emoji: '📅' },
  time:          { bg: 'var(--blue-bg)',   border: 'var(--blue-border)',   text: 'text-info',    emoji: '⏰' },
  url:           { bg: 'var(--bg3)',       border: 'var(--border)',        text: 'text-text2',   emoji: '🔗' },
  anyNumber:     { bg: 'var(--bg3)',       border: 'var(--border)',        text: 'text-text2',   emoji: '#' },
  anyText:       { bg: 'var(--bg3)',       border: 'var(--border)',        text: 'text-text2',   emoji: '⋯' },
  anyLine:       { bg: 'var(--bg3)',       border: 'var(--border)',        text: 'text-text2',   emoji: '¶' },
  flagEmoji:     { bg: 'var(--bg3)',       border: 'var(--border)',        text: 'text-text2',   emoji: '🌍' },
};

/**
 * Inline-редактор шаблона: текст и chips в одном потоке.
 * Реализован через contentEditable div:
 *  - Литералы — обычные текстовые узлы (редактируются inline).
 *  - Placeholders — `<span contenteditable=false data-type=... data-name=...>` chips.
 *  - Поле растёт по высоте автоматически (min-height + height: auto).
 *  - Чтобы добавить переменную: выдели мышью кусок в любом литерале и кликни тип.
 *  - Чтобы удалить/сменить тип: клик по chip → popover.
 *  - При любом ручном редактировании текста — парсим DOM обратно в segments.
 */
function SegmentBuilder({
  sample,
  segments,
  onChange,
}: {
  sample: string;
  segments: Segment[];
  onChange: (s: Segment[]) => void;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Сохранённая Selection range для случая когда фокус ушёл на toolbar-кнопку.
  const savedRange = useRef<Range | null>(null);
  // Чтобы не перерендеривать DOM при каждом input (caret прыгал бы) —
  // отслеживаем какие segments последний раз были применены изнутри.
  const lastAppliedSegmentsRef = useRef<Segment[]>(segments);

  // Инициализация: если сейчас segments пустой — заполняем sample как один literal.
  useEffect(() => {
    if (segments.length === 0 && sample) {
      onChange([{ kind: 'literal', text: sample }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sample]);

  // Рендер segments в DOM editor'а. Делаем только если segments пришли извне
  // (template-загрузка, undo) и отличаются от того что мы последний раз применили
  // изнутри (чтобы не сбрасывать caret при input/typing).
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (segmentsEqual(segments, lastAppliedSegmentsRef.current) && el.childNodes.length > 0) {
      return; // DOM уже актуален
    }
    renderSegmentsToDom(el, segments);
    lastAppliedSegmentsRef.current = segments;
  }, [segments]);

  function captureSelection() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  }

  function parseDomToSegments(): Segment[] {
    const el = editorRef.current;
    if (!el) return segments;
    const result: Segment[] = [];
    for (const node of Array.from(el.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (text.length === 0) continue;
        const last = result[result.length - 1];
        if (last && last.kind === 'literal') last.text += text;
        else result.push({ kind: 'literal', text });
      } else if (node instanceof HTMLElement && node.dataset.chip === '1') {
        const type = node.dataset.type as VariableType | undefined;
        const name = node.dataset.name;
        if (type && name) {
          result.push({ kind: 'placeholder', type, name });
        }
      } else if (node instanceof HTMLElement) {
        // Может быть <br> или вложенные элементы — берём textContent.
        const text = node.textContent ?? '';
        if (text.length === 0) continue;
        const last = result[result.length - 1];
        if (last && last.kind === 'literal') last.text += text;
        else result.push({ kind: 'literal', text });
      }
    }
    return result;
  }

  function handleInput() {
    // Пользователь правил литералы — собираем segments из DOM.
    const next = parseDomToSegments();
    lastAppliedSegmentsRef.current = next;
    onChange(next);
  }

  function addPlaceholderFromSelection(type: VariableType) {
    setWarning(null);
    const el = editorRef.current;
    if (!el) return;

    // Восстанавливаем сохранённую selection (т.к. фокус мог уйти на toolbar).
    const sel = window.getSelection();
    let range: Range | null = null;
    if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
      range = sel.getRangeAt(0);
    } else if (savedRange.current && el.contains(savedRange.current.startContainer)) {
      range = savedRange.current;
    }

    if (!range || range.collapsed) {
      setWarning('Сначала выдели мышкой кусок текста в шаблоне сверху');
      return;
    }

    const name = uniqueName(type, segments);
    const chip = createChipElement(type, name);
    range.deleteContents();
    range.insertNode(chip);

    // Курсор после chip.
    const newRange = document.createRange();
    newRange.setStartAfter(chip);
    newRange.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(newRange);

    // Парсим обратно и пушим в state.
    const next = parseDomToSegments();
    lastAppliedSegmentsRef.current = next;
    onChange(next);
  }

  function reset() {
    onChange([]);
    setWarning(null);
  }

  function removeChipByName(name: string) {
    const next = segments.filter((s) => !(s.kind === 'placeholder' && s.name === name));
    // Schedule re-render in next tick to align lastAppliedSegmentsRef.
    lastAppliedSegmentsRef.current = []; // force re-render
    onChange(mergeAdjacentLiterals(next));
  }

  function changeChipType(name: string, newType: VariableType) {
    const next = segments.map((s) =>
      s.kind === 'placeholder' && s.name === name
        ? { ...s, type: newType, name: uniqueName(newType, segments.filter((x) => x !== s)) }
        : s,
    );
    lastAppliedSegmentsRef.current = []; // force re-render
    onChange(next);
  }

  // Делегированный click handler для chip-popover'ов.
  const [chipMenu, setChipMenu] = useState<{ name: string; x: number; y: number } | null>(null);

  function handleEditorClick(e: React.MouseEvent) {
    const target = e.target as HTMLElement;
    const chip = target.closest('[data-chip="1"]') as HTMLElement | null;
    if (chip && chip.dataset.name) {
      e.preventDefault();
      const rect = chip.getBoundingClientRect();
      setChipMenu({
        name: chip.dataset.name,
        x: rect.left,
        y: rect.bottom + 4,
      });
    } else {
      setChipMenu(null);
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[11px] text-text-subtle">
            Шаблон сообщения. Выдели мышью кусок текста и кликни тип ниже.
          </span>
          {segments.length > 0 && (
            <button onClick={reset} className="text-[10px] text-text-subtle hover:text-text">
              ↺ Сбросить разметку
            </button>
          )}
        </div>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onMouseUp={captureSelection}
          onKeyUp={captureSelection}
          onBlur={captureSelection}
          onClick={handleEditorClick}
          className={`min-h-[60px] w-full whitespace-pre-wrap break-words rounded-md border border-border bg-bg p-3 font-mono text-xs leading-6 text-text outline-none transition focus:border-accent focus:shadow-focus`}
          style={{ wordBreak: 'break-word' }}
        />
      </div>

      {/* Toolbar */}
      <div>
        <div className="mb-1.5 text-[11px] uppercase tracking-wide text-text-subtle">
          Добавить переменную
        </div>
        <div className="flex flex-wrap gap-1.5">
          {VARIABLE_TYPES.map((t) => (
            <TypeButton
              key={t}
              type={t}
              onMouseDown={(e) => {
                // Сохраняем выделение ДО потери focus в editor.
                captureSelection();
                e.preventDefault();
              }}
              onClick={() => addPlaceholderFromSelection(t)}
            />
          ))}
        </div>
        {warning && (
          <div className="mt-2 rounded-md border border-[var(--yellow-border)] bg-[var(--yellow-bg)] px-2 py-1 text-[11px] text-warning">
            {warning}
          </div>
        )}
      </div>

      {segments.some((s) => s.kind === 'placeholder') && <VariableSummary segments={segments} />}

      {chipMenu && (
        <ChipMenu
          menu={chipMenu}
          onClose={() => setChipMenu(null)}
          onChangeType={(t) => {
            changeChipType(chipMenu.name, t);
            setChipMenu(null);
          }}
          onRemove={() => {
            removeChipByName(chipMenu.name);
            setChipMenu(null);
          }}
        />
      )}
    </div>
  );
}

// ─── DOM helpers для contenteditable редактора ─────────────────────────────

function segmentsEqual(a: Segment[], b: Segment[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (x.kind !== y.kind) return false;
    if (x.kind === 'literal' && y.kind === 'literal' && x.text !== y.text) return false;
    if (x.kind === 'placeholder' && y.kind === 'placeholder' && (x.name !== y.name || x.type !== y.type))
      return false;
  }
  return true;
}

function mergeAdjacentLiterals(segs: Segment[]): Segment[] {
  const out: Segment[] = [];
  for (const s of segs) {
    const last = out[out.length - 1];
    if (last && last.kind === 'literal' && s.kind === 'literal') {
      out[out.length - 1] = { kind: 'literal', text: last.text + s.text };
    } else {
      out.push(s);
    }
  }
  return out;
}

function createChipElement(type: VariableType, name: string): HTMLSpanElement {
  const style = TYPE_STYLE[type];
  const chip = document.createElement('span');
  chip.dataset.chip = '1';
  chip.dataset.type = type;
  chip.dataset.name = name;
  chip.contentEditable = 'false';
  chip.className = 'inline-flex items-center gap-1 rounded-md border px-1.5 py-0 mx-0.5 text-[11px] font-medium align-baseline cursor-pointer select-none';
  chip.style.backgroundColor = style.bg;
  chip.style.borderColor = style.border;
  chip.innerHTML = `<span class="${style.text}">${style.emoji}</span><span class="${style.text}">${VARIABLE_TYPE_LABELS[type]}</span><span class="text-[9px] text-text-subtle">▾</span>`;
  return chip;
}

function renderSegmentsToDom(el: HTMLElement, segments: Segment[]): void {
  el.innerHTML = '';
  for (const seg of segments) {
    if (seg.kind === 'literal') {
      el.appendChild(document.createTextNode(seg.text));
    } else {
      el.appendChild(createChipElement(seg.type, seg.name));
    }
  }
  // Если пусто — добавляем пустой text node чтобы был initial caret position.
  if (segments.length === 0) {
    el.appendChild(document.createTextNode(''));
  }
}

function ChipMenu({
  menu,
  onClose,
  onChangeType,
  onRemove,
}: {
  menu: { name: string; x: number; y: number };
  onClose: () => void;
  onChangeType: (t: VariableType) => void;
  onRemove: () => void;
}) {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const tgt = e.target as HTMLElement;
      if (!tgt.closest('[data-chip-menu="1"]')) onClose();
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [onClose]);
  return createPortal(
    <div
      data-chip-menu="1"
      className="fixed z-50 w-52 rounded-md border border-border bg-bg p-1 shadow-lg"
      style={{ left: menu.x, top: menu.y }}
    >
      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-text-subtle">
        Изменить тип
      </div>
      <div className="max-h-64 overflow-y-auto">
        {VARIABLE_TYPES.map((t) => {
          const s = TYPE_STYLE[t];
          return (
            <button
              key={t}
              type="button"
              onClick={() => onChangeType(t)}
              className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-bg2"
            >
              <span className={s.text}>{s.emoji}</span>
              <span className="text-text">{VARIABLE_TYPE_LABELS[t]}</span>
            </button>
          );
        })}
      </div>
      <div className="my-1 border-t border-border-subtle" />
      <button
        type="button"
        onClick={onRemove}
        className="block w-full rounded px-2 py-1 text-left text-xs text-danger hover:bg-[var(--red-bg)]"
      >
        ✕ Удалить переменную
      </button>
    </div>,
    document.body,
  );
}

/**
 * Textarea, которая автоматически растёт по высоте под содержимое.
 * Используется для sample-инпута, чтобы оператор видел длинное сообщение
 * целиком без скролла внутри узкого поля.
 */
function AutoGrowTextarea({
  value,
  onChange,
  placeholder,
  minRows = 2,
  maxRows = 12,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = 18; // соответствует tailwind text-xs leading-snug ≈ 18px
    const minH = minRows * lineHeight + 16;
    const maxH = maxRows * lineHeight + 16;
    el.style.height = `${Math.max(minH, Math.min(maxH, el.scrollHeight))}px`;
  }, [value, minRows, maxRows]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={minRows}
      placeholder={placeholder}
      className={`${textareaCls} w-full overflow-hidden font-mono text-xs leading-snug`}
    />
  );
}

function TypeButton({
  type,
  onClick,
  onMouseDown,
}: {
  type: VariableType;
  onClick: () => void;
  onMouseDown?: (e: React.MouseEvent) => void;
}) {
  const style = TYPE_STYLE[type];
  return (
    <button
      type="button"
      onMouseDown={onMouseDown}
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors hover:opacity-80"
      style={{
        backgroundColor: style.bg,
        borderColor: style.border,
      }}
    >
      <span className={style.text}>{style.emoji}</span>
      <span className={style.text}>{VARIABLE_TYPE_LABELS[type]}</span>
    </button>
  );
}

function Chip({
  segment,
  onRemove,
  onChangeType,
}: {
  segment: { kind: 'placeholder'; type: VariableType; name: string };
  onRemove: () => void;
  onChangeType: (t: VariableType) => void;
}) {
  const [open, setOpen] = useState(false);
  const style = TYPE_STYLE[segment.type];
  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors"
        style={{
          backgroundColor: style.bg,
          borderColor: style.border,
        }}
        title="Изменить тип / удалить"
      >
        <span className={style.text}>{style.emoji}</span>
        <span className={style.text}>{VARIABLE_TYPE_LABELS[segment.type]}</span>
        <span className="text-[9px] text-text-subtle">▾</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 w-52 rounded-md border border-border bg-bg p-1 shadow-lg">
          <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-text-subtle">
            Изменить тип
          </div>
          {VARIABLE_TYPES.map((t) => {
            const s = TYPE_STYLE[t];
            return (
              <button
                key={t}
                type="button"
                onClick={() => {
                  onChangeType(t);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left text-xs hover:bg-bg2 ${
                  segment.type === t ? 'bg-bg2 font-semibold' : ''
                }`}
              >
                <span className={s.text}>{s.emoji}</span>
                <span className="text-text">{VARIABLE_TYPE_LABELS[t]}</span>
              </button>
            );
          })}
          <div className="my-1 border-t border-border-subtle" />
          <button
            type="button"
            onClick={() => {
              onRemove();
              setOpen(false);
            }}
            className="block w-full rounded px-2 py-1 text-left text-xs text-danger hover:bg-[var(--red-bg)]"
          >
            ✕ Удалить переменную
          </button>
        </div>
      )}
    </span>
  );
}

function VariableSummary({ segments }: { segments: Segment[] }) {
  const placeholders = segments.filter((s) => s.kind === 'placeholder') as Array<{
    kind: 'placeholder';
    type: VariableType;
    name: string;
  }>;
  if (placeholders.length === 0) return null;
  return (
    <div className="rounded-md border border-border-subtle bg-bg p-2.5">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-subtle">
        Что вытащим из сообщения
      </div>
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {placeholders.map((p, i) => {
          const style = TYPE_STYLE[p.type];
          return (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span
                className="inline-flex h-5 items-center gap-1 rounded border px-1.5"
                style={{ backgroundColor: style.bg, borderColor: style.border }}
              >
                <span className={style.text}>{style.emoji}</span>
              </span>
              <span className="text-text">{VARIABLE_TYPE_LABELS[p.type]}</span>
              <span className="font-mono text-[10px] text-text-subtle">{p.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function uniqueName(type: VariableType, segments: Segment[]): string {
  const existing = new Set(
    segments.filter((s) => s.kind === 'placeholder').map((s) => (s as { name: string }).name),
  );
  let i = 1;
  let candidate: string = type;
  while (existing.has(candidate)) {
    i++;
    candidate = `${type}${i}`;
  }
  return candidate;
}

// ════════════════════════════════════════════════════════════════════════════
// Шаг 3: Действие, превью результата, чаты, название
// ════════════════════════════════════════════════════════════════════════════

function ActionAndChats({
  actionType,
  onChangeActionType,
  autoSuggested,
  handlers,
  segments,
  testResult,
  sample,
  chats,
  chatIds,
  onChangeChatIds,
  name,
  onChangeName,
  description,
  onChangeDescription,
}: {
  actionType: TelegramActionType;
  onChangeActionType: (t: TelegramActionType) => void;
  autoSuggested: boolean;
  handlers: { type: string; name: string; description: string }[];
  segments: Segment[];
  testResult: {
    ok: boolean;
    error: string | null;
    matches: Array<{ match: string; groups: Record<string, string> }>;
  } | null;
  sample: string;
  chats: { chatId: string; chatTitle: string | null; enabled: boolean }[];
  chatIds: string[];
  onChangeChatIds: (v: string[]) => void;
  name: string;
  onChangeName: (v: string) => void;
  description: string;
  onChangeDescription: (v: string) => void;
}) {
  const currentHandler = handlers.find((h) => h.type === actionType);

  return (
    <div className="space-y-3">
      {/* Action type picker */}
      <div>
        <div className="mb-1.5 flex items-baseline justify-between">
          <label className={fieldLabelCls}>Действие</label>
          {autoSuggested && (
            <span className="text-[10px] text-text-subtle">подобрано автоматически</span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {handlers.map((h) => (
            <button
              key={h.type}
              type="button"
              onClick={() => onChangeActionType(h.type as TelegramActionType)}
              className={`rounded-md border px-3 py-2 text-left transition-colors ${
                actionType === h.type
                  ? 'border-[var(--accent-border)] bg-[var(--accent-bg)]'
                  : 'border-border bg-bg hover:border-border-strong'
              }`}
            >
              <div className={`text-sm font-medium ${actionType === h.type ? 'text-accent' : 'text-text'}`}>
                {h.name}
              </div>
              <div className="mt-0.5 text-[11px] text-text-subtle">{h.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Превью результата работы */}
      <FlowPreview
        sample={sample}
        segments={segments}
        testResult={testResult}
        actionType={actionType}
        actionName={currentHandler?.name ?? actionType}
      />

      {/* Чаты */}
      <div>
        <label className={fieldLabelCls}>Чаты, в которых работает правило</label>
        <div className="max-h-36 space-y-1 overflow-y-auto rounded-md border border-border bg-bg p-1.5">
          {chats.length === 0 ? (
            <div className="px-2 py-1 text-xs text-text-subtle">
              Сначала зарегистрируй чаты на вкладке «Чаты»
            </div>
          ) : (
            chats.map((c) => (
              <label
                key={c.chatId}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-text hover:bg-bg2"
              >
                <input
                  type="checkbox"
                  checked={chatIds.includes(c.chatId)}
                  onChange={(e) => {
                    if (e.target.checked) onChangeChatIds([...chatIds, c.chatId]);
                    else onChangeChatIds(chatIds.filter((id) => id !== c.chatId));
                  }}
                />
                <span className="flex-1">{c.chatTitle ?? <span className="text-text-subtle">— без названия —</span>}</span>
                <span className="font-mono text-[10px] text-text-subtle">{c.chatId}</span>
                {!c.enabled && <span className="text-[10px] text-text-subtle">(выкл)</span>}
              </label>
            ))
          )}
        </div>
      </div>

      {/* Название + описание */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className={fieldLabelCls}>Название правила</label>
          <input
            value={name}
            onChange={(e) => onChangeName(e.target.value)}
            className={inputCls}
            placeholder="Например: Тинькофф — поступление RUB"
          />
        </div>
        <div>
          <label className={fieldLabelCls}>Описание (необязательно)</label>
          <input
            value={description}
            onChange={(e) => onChangeDescription(e.target.value)}
            className={inputCls}
            placeholder="Для чего это правило"
          />
        </div>
      </div>
    </div>
  );
}

/** Наглядное превью «что произойдёт, когда правило сработает». */
function FlowPreview({
  sample,
  segments,
  testResult,
  actionType,
  actionName,
}: {
  sample: string;
  segments: Segment[];
  testResult: {
    ok: boolean;
    error: string | null;
    matches: Array<{ match: string; groups: Record<string, string> }>;
  } | null;
  actionType: TelegramActionType;
  actionName: string;
}) {
  const placeholders = segments.filter((s) => s.kind === 'placeholder') as Array<{
    kind: 'placeholder';
    type: VariableType;
    name: string;
  }>;
  const matched = testResult?.ok && testResult.matches.length > 0;
  const groups = matched ? testResult.matches[0]?.groups ?? {} : {};

  // Дружелюбное описание действия с подстановкой captures
  function actionSentence(): string {
    const get = (name: string) => groups[name] ?? `[${name}]`;
    switch (actionType) {
      case 'update-rate':
        return `Обновить курс ${get(placeholders.find((p) => p.type === 'ccy')?.name ?? 'ccy')} → ${get(placeholders.find((p) => p.type === 'amount')?.name ?? 'rate')} USDT`;
      case 'update-chat-balance':
        return `Обновить баланс чата: ${get(placeholders.find((p) => p.type === 'ccy')?.name ?? 'ccy')} → ${get(placeholders.find((p) => p.type === 'amount')?.name ?? 'amount')}`;
      case 'confirm-provodka':
        return `Подтвердить проводку заявки ${get(placeholders.find((p) => p.type === 'orderId')?.name ?? 'orderId')}`;
      case 'create-order':
        return 'Создать новую заявку по данным сообщения';
      case 'log-only':
        return 'Записать совпадение в журнал (без действий)';
      case 'send-message':
        return 'Отправить сообщение в указанные чаты по шаблону';
      case 'reject-order':
        return `Отклонить заявку ${get(placeholders.find((p) => p.type === 'orderId')?.name ?? 'orderId')}`;
      case 'edit-provodka':
        return `Изменить проводку заявки ${get(placeholders.find((p) => p.type === 'orderId')?.name ?? 'orderId')}`;
      case 'custom-webhook':
        return 'Отправить запрос на внешний webhook';
    }
  }

  return (
    <div className="rounded-md border border-border bg-bg p-3">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-text-subtle">
        Что произойдёт
      </div>
      <div className="space-y-2 text-xs">
        <FlowStep icon="📥" label="Бот видит сообщение">
          <span className="font-mono text-text">{sample || '—'}</span>
        </FlowStep>
        <FlowArrow />
        <FlowStep
          icon="🎯"
          label={matched ? 'Распознаёт' : testResult ? 'Не распознал — проверь разметку' : 'Распознает'}
          tone={matched ? 'ok' : testResult && !matched ? 'warn' : 'idle'}
        >
          {placeholders.length === 0 ? (
            <span className="text-text-subtle">пока нет переменных</span>
          ) : (
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {placeholders.map((p) => {
                const style = TYPE_STYLE[p.type];
                const value = groups[p.name];
                return (
                  <span key={p.name} className="inline-flex items-baseline gap-1">
                    <span className={style.text}>{style.emoji}</span>
                    <span className="text-text2">{VARIABLE_TYPE_LABELS[p.type]}:</span>
                    <span className="font-mono text-text">
                      {value ?? <span className="text-text-subtle">?</span>}
                    </span>
                  </span>
                );
              })}
            </div>
          )}
        </FlowStep>
        <FlowArrow />
        <FlowStep icon="✅" label={`Действие: ${actionName}`} tone="accent">
          <span className="text-text">{actionSentence()}</span>
        </FlowStep>
      </div>
    </div>
  );
}

function FlowStep({
  icon,
  label,
  tone = 'idle',
  children,
}: {
  icon: string;
  label: string;
  tone?: 'idle' | 'ok' | 'warn' | 'accent';
  children: React.ReactNode;
}) {
  const toneCls = {
    idle: 'border-border bg-bg2',
    ok: 'border-[var(--green-border)] bg-[var(--green-bg)]',
    warn: 'border-[var(--yellow-border)] bg-[var(--yellow-bg)]',
    accent: 'border-[var(--accent-border)] bg-[var(--accent-bg)]',
  }[tone];
  return (
    <div className={`flex items-start gap-2.5 rounded-md border p-2.5 ${toneCls}`}>
      <span className="mt-0.5 text-base leading-none">{icon}</span>
      <div className="flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-text2">{label}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="flex justify-center">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        className="text-text-subtle"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 5v14M19 12l-7 7-7-7" />
      </svg>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Расширенный режим (raw pattern + JSON config)
// ════════════════════════════════════════════════════════════════════════════

function AdvancedFields({
  pattern,
  setPattern,
  flags,
  setFlags,
  actionConfigJson,
  setActionConfigJson,
  sample,
  setSample,
  testResult,
  handlerConfigSchema,
  actionType,
  handlers,
  onChangeActionType,
  name,
  onChangeName,
  chats,
  chatIds,
  onChangeChatIds,
  dryRunPanel,
}: {
  pattern: string;
  setPattern: (v: string) => void;
  flags: string;
  setFlags: (v: string) => void;
  actionConfigJson: string;
  setActionConfigJson: (v: string) => void;
  sample: string;
  setSample: (v: string) => void;
  testResult: {
    ok: boolean;
    error: string | null;
    matches: Array<{ match: string; groups: Record<string, string> }>;
  } | null;
  handlerConfigSchema: Record<string, unknown> | null;
  actionType: TelegramActionType;
  handlers: { type: string; name: string }[];
  onChangeActionType: (t: TelegramActionType) => void;
  name: string;
  onChangeName: (v: string) => void;
  chats: { chatId: string; chatTitle: string | null; enabled: boolean }[];
  chatIds: string[];
  onChangeChatIds: (v: string[]) => void;
  dryRunPanel?: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className={cardL2}>
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-subtle">
          Расширенный режим — прямое редактирование regex
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className={fieldLabelCls}>Название</label>
            <input value={name} onChange={(e) => onChangeName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={fieldLabelCls}>Действие</label>
            <select
              value={actionType}
              onChange={(e) => onChangeActionType(e.target.value as TelegramActionType)}
              className={inputCls}
            >
              {handlers.map((h) => (
                <option key={h.type} value={h.type}>
                  {h.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
          <div>
            <label className={fieldLabelCls}>Pattern (regex)</label>
            <input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              className={`${inputCls} font-mono text-xs`}
            />
          </div>
          <div>
            <label className={fieldLabelCls}>Flags</label>
            <input
              value={flags}
              onChange={(e) => setFlags(e.target.value.replace(/[^gimsuy]/g, ''))}
              className={`${inputCls} w-20 text-center font-mono`}
              maxLength={6}
            />
          </div>
        </div>
        <div className="mt-3">
          <label className={fieldLabelCls}>actionConfig (JSON)</label>
          <textarea
            value={actionConfigJson}
            onChange={(e) => setActionConfigJson(e.target.value)}
            rows={Math.min(8, actionConfigJson.split('\n').length + 1)}
            className={`${textareaCls} w-full font-mono text-xs`}
          />
          {handlerConfigSchema && (
            <details className="mt-1 text-[10px] text-text-subtle">
              <summary className="cursor-pointer">Схема параметров</summary>
              <pre className="mt-1 overflow-x-auto rounded bg-bg3 p-2 font-mono">
                {JSON.stringify(handlerConfigSchema, null, 2)}
              </pre>
            </details>
          )}
        </div>
        <div className="mt-3">
          <label className={fieldLabelCls}>Тестер</label>
          <textarea
            value={sample}
            onChange={(e) => setSample(e.target.value)}
            rows={2}
            className={`${textareaCls} w-full font-mono text-xs`}
            placeholder="Получено 50000 RUB по заявке W-001"
          />
          {testResult && (
            <div className="mt-2">
              <TestMatchesList testResult={testResult} />
            </div>
          )}
        </div>
        {dryRunPanel && (
          <div className="mt-3 border-t border-border-subtle pt-3">{dryRunPanel}</div>
        )}
      </div>

      <div className={cardL2}>
        <label className={fieldLabelCls}>Чаты</label>
        <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border bg-bg p-1.5">
          {chats.map((c) => (
            <label
              key={c.chatId}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-text hover:bg-bg2"
            >
              <input
                type="checkbox"
                checked={chatIds.includes(c.chatId)}
                onChange={(e) => {
                  if (e.target.checked) onChangeChatIds([...chatIds, c.chatId]);
                  else onChangeChatIds(chatIds.filter((id) => id !== c.chatId));
                }}
              />
              <span>{c.chatTitle ?? '—'}</span>
              <span className="font-mono text-[10px] text-text-subtle">{c.chatId}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Dry-run: «⚡ Полная проверка» + «📜 Проверить на последних сообщениях»
// ════════════════════════════════════════════════════════════════════════════

/**
 * Панель полной проверки правила БЕЗ записи: regex → guards → validate всех
 * действий цепочки (POST /telegram/rules/dry-run). Два режима:
 *  - sample: прогон текущего примера (как обычный тестер, но с guards/chain);
 *  - messages: ретро-прогон последних 20 сообщений message_log выбранного чата.
 */
function DryRunPanel({
  pattern,
  flags,
  guards,
  getActions,
  sample,
  chatId,
  handlers,
}: {
  pattern: string;
  flags: string;
  guards: RuleGuard[];
  /** Актуальная цепочка actions либо текст ошибки (invalid JSON в advanced). */
  getActions: () => RuleAction[] | { error: string };
  sample: string;
  chatId: string | null;
  handlers: { type: string; name: string }[];
}) {
  const dryRunMut = useDryRunRule();
  const [mode, setMode] = useState<'sample' | 'messages' | null>(null);
  const [result, setResult] = useState<RuleDryRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(target: 'sample' | 'messages') {
    setError(null);
    setResult(null);
    const actions = getActions();
    if (!Array.isArray(actions)) {
      setError(actions.error);
      setMode(null);
      return;
    }
    if (actions.length === 0) {
      setError('Сначала добавь хотя бы одно действие');
      setMode(null);
      return;
    }
    setMode(target);
    try {
      const res = await dryRunMut.mutateAsync({
        pattern,
        flags,
        actions,
        guards,
        ...(chatId ? { chatId } : {}),
        ...(target === 'sample' ? { sample } : { messageLimit: 20 }),
      });
      setResult(res);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const matchedCount =
    mode === 'messages' && result
      ? result.results.filter((r) => r.matches.length > 0).length
      : 0;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void run('sample')}
          disabled={dryRunMut.isPending || !pattern || !sample}
          className={btnSecondarySm}
          title={
            sample
              ? 'Прогнать пример через guards и все действия цепочки (без записи)'
              : 'Сначала выбери пример сообщения'
          }
        >
          ⚡ Полная проверка
        </button>
        <button
          type="button"
          onClick={() => void run('messages')}
          disabled={dryRunMut.isPending || !pattern || !chatId}
          className={btnSecondarySm}
          title={
            chatId
              ? 'Прогнать правило по последним 20 сообщениям выбранного чата'
              : 'Сначала выбери чат правила'
          }
        >
          📜 Проверить на последних сообщениях
        </button>
        {dryRunMut.isPending && (
          <span className="text-[11px] text-text-subtle">Проверяем…</span>
        )}
      </div>
      <div className="mt-1 text-[10px] text-text-subtle">
        Так событие будет обработано в бою: guards → проверка действий. Ничего не записывается.
      </div>

      {error && (
        <div className="mt-2 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-2 py-1.5 text-[11px] text-danger">
          ✗ {error}
        </div>
      )}

      {result && mode === 'sample' && (
        <div className="mt-2">
          {(result.results[0]?.matches.length ?? 0) === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-bg px-2 py-1.5 text-[11px] text-text2">
              Нет совпадений — правило не сработает на этом примере.
            </div>
          ) : (
            <DryRunMatchList matches={result.results[0]!.matches} handlers={handlers} />
          )}
        </div>
      )}

      {result && mode === 'messages' && (
        <div className="mt-2">
          {result.results.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-bg px-2 py-1.5 text-[11px] text-text-subtle">
              В журнале этого чата пока нет сообщений — бот ещё ничего не записал. Убедись, что
              бот добавлен в чат и слушание включено.
            </div>
          ) : (
            <div className="space-y-1">
              <div className="text-[10px] text-text-subtle">
                Проверено сообщений: {result.results.length}, с совпадениями: {matchedCount}
              </div>
              {result.results.map((item, i) => (
                <DryRunMessageRow key={item.messageLogId ?? `i${i}`} item={item} handlers={handlers} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DryRunMatchList({
  matches,
  handlers,
}: {
  matches: RuleDryRunMatch[];
  handlers: { type: string; name: string }[];
}) {
  return (
    <div className="space-y-1.5">
      {matches.map((m, i) => (
        <DryRunMatchCard key={i} match={m} index={i} total={matches.length} handlers={handlers} />
      ))}
    </div>
  );
}

/** Один матч: captures → guards → цепочка действий (каждый шаг ✓/✗ + preview). */
function DryRunMatchCard({
  match,
  index,
  total,
  handlers,
}: {
  match: RuleDryRunMatch;
  index: number;
  total: number;
  handlers: { type: string; name: string }[];
}) {
  const handlerName = (type: string) => handlers.find((h) => h.type === type)?.name ?? type;
  return (
    <div className="rounded-md border border-border bg-bg p-2 text-[11px]">
      {total > 1 && (
        <div className="mb-1 text-[10px] font-semibold text-text-subtle">
          Совпадение #{index + 1}
        </div>
      )}
      {Object.keys(match.groups).length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono">
          {Object.entries(match.groups).map(([k, v]) => (
            <span key={k}>
              <span className="text-text-subtle">{k}=</span>
              <span className="text-text">{v}</span>
            </span>
          ))}
        </div>
      )}
      <div
        className={`mt-1.5 flex items-start gap-1.5 rounded px-1.5 py-1 ${
          match.guardsOk
            ? 'bg-[var(--green-bg)] text-success'
            : 'bg-[var(--yellow-bg)] text-warning'
        }`}
      >
        <span className="shrink-0">{match.guardsOk ? '✓' : '⚠'}</span>
        <span>
          {match.guardsOk
            ? 'Guards пройдены'
            : `Отфильтровано: ${match.guardsReason ?? 'условие не выполнено'}`}
        </span>
      </div>
      {match.chain.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {match.chain.map((step, si) => (
            <div
              key={si}
              className={`rounded border px-1.5 py-1 ${
                step.ok
                  ? 'border-[var(--green-border)] bg-[var(--green-bg)]'
                  : 'border-[var(--red-border)] bg-[var(--red-bg)]'
              }`}
            >
              <div className="flex flex-wrap items-baseline gap-1.5">
                <span className={step.ok ? 'text-success' : 'text-danger'}>
                  {step.ok ? '✓' : '✗'}
                </span>
                <span className="font-medium text-text">
                  {si + 1}. {handlerName(step.type)}
                </span>
                {!step.ok && step.error && <span className="text-danger">{step.error}</span>}
              </div>
              {step.ok && step.preview !== undefined && step.preview !== null && (
                <div className="mt-0.5 pl-4">
                  <DryRunStepPreview preview={step.preview} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Человекочитаемый preview шага: `note` — приоритетно, простые поля — парами
 * key=value, вложенные структуры — fallback JSON в <pre>.
 */
function DryRunStepPreview({ preview }: { preview: unknown }) {
  if (preview === null || preview === undefined) return null;
  if (
    typeof preview === 'string' ||
    typeof preview === 'number' ||
    typeof preview === 'boolean'
  ) {
    return <div className="text-text2">{String(preview)}</div>;
  }
  if (typeof preview === 'object' && !Array.isArray(preview)) {
    const record = preview as Record<string, unknown>;
    const note = typeof record.note === 'string' ? record.note : null;
    const isSimple = (v: unknown) =>
      v === null || ['string', 'number', 'boolean'].includes(typeof v);
    const entries = Object.entries(record).filter(([k]) => k !== 'note');
    const simple = entries.filter(([, v]) => isSimple(v));
    const complex = entries.filter(([, v]) => !isSimple(v));
    return (
      <div className="space-y-0.5">
        {note && <div className="text-text">{note}</div>}
        {simple.length > 0 && (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[10px]">
            {simple.map(([k, v]) => (
              <span key={k}>
                <span className="text-text-subtle">{k}=</span>
                <span className="text-text">{v === null ? '—' : String(v)}</span>
              </span>
            ))}
          </div>
        )}
        {complex.length > 0 && (
          <pre className="max-h-40 overflow-auto rounded bg-bg3 p-1.5 font-mono text-[10px] text-text2">
            {JSON.stringify(Object.fromEntries(complex), null, 2)}
          </pre>
        )}
      </div>
    );
  }
  return (
    <pre className="max-h-40 overflow-auto rounded bg-bg3 p-1.5 font-mono text-[10px] text-text2">
      {JSON.stringify(preview, null, 2)}
    </pre>
  );
}

/** Русская плюрализация «N матч/матча/матчей». */
function pluralMatches(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} матч`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} матча`;
  return `${n} матчей`;
}

/** Строка ретро-прогона: время + текст (truncate, разворачиваемый) + бейдж матчей. */
function DryRunMessageRow({
  item,
  handlers,
}: {
  item: RuleDryRunResultItem;
  handlers: { type: string; name: string }[];
}) {
  const [open, setOpen] = useState(false);
  const n = item.matches.length;
  const time = item.receivedAt
    ? new Date(item.receivedAt).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
  return (
    <div className="rounded-md border border-border bg-bg">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[11px] transition-colors hover:bg-bg2"
      >
        <span className="shrink-0 font-mono text-[10px] text-text-subtle">{time ?? '—'}</span>
        <span
          className={`min-w-0 flex-1 text-text2 ${
            open ? 'whitespace-pre-wrap break-words' : 'truncate'
          }`}
        >
          {item.text || '— без текста —'}
        </span>
        {n > 0 ? (
          <span className="shrink-0 rounded-full border border-[var(--green-border)] bg-[var(--green-bg)] px-1.5 py-0.5 text-[10px] font-semibold text-success">
            {pluralMatches(n)}
          </span>
        ) : (
          <span className="shrink-0 rounded-full border border-border bg-bg3 px-1.5 py-0.5 text-[10px] text-text-subtle">
            нет
          </span>
        )}
        <span className="shrink-0 text-[10px] text-text-subtle">{open ? '▾' : '▸'}</span>
      </button>
      {open &&
        (n > 0 ? (
          <div className="border-t border-border-subtle p-2">
            <DryRunMatchList matches={item.matches} handlers={handlers} />
          </div>
        ) : (
          <div className="border-t border-border-subtle p-2 text-[11px] text-text-subtle">
            Regex не совпал с этим сообщением.
          </div>
        ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// v2: ExtraActionsEditor — список доп. действий цепочки (action[1..N])
// ════════════════════════════════════════════════════════════════════════════

function ExtraActionsEditor({
  extraActions,
  onChange,
  handlers,
  segments,
}: {
  extraActions: RuleAction[];
  onChange: (a: RuleAction[]) => void;
  handlers: { type: string; name: string; description: string }[];
  segments: Segment[];
}) {
  function addAction() {
    onChange([...extraActions, { type: 'log-only', config: {} }]);
  }
  function removeAt(i: number) {
    onChange(extraActions.filter((_, idx) => idx !== i));
  }
  function updateAt(i: number, patch: Partial<RuleAction>) {
    onChange(extraActions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-text2">
          Цепочка действий (после первого)
        </div>
        <button type="button" onClick={addAction} className="text-xs text-accent hover:underline">
          + Добавить действие
        </button>
      </div>
      {extraActions.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-bg px-3 py-2 text-xs text-text-subtle">
          Цепочка пуста. Можно добавить, например, отправку уведомления в чат менеджеров после
          подтверждения проводки.
        </div>
      ) : (
        <div className="space-y-2">
          {extraActions.map((a, i) => (
            <ExtraActionCard
              key={i}
              index={i}
              action={a}
              handlers={handlers}
              segments={segments}
              onChange={(patch) => updateAt(i, patch)}
              onRemove={() => removeAt(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ExtraActionCard({
  index,
  action,
  handlers,
  segments,
  onChange,
  onRemove,
}: {
  index: number;
  action: RuleAction;
  handlers: { type: string; name: string; description: string }[];
  segments: Segment[];
  onChange: (patch: Partial<RuleAction>) => void;
  onRemove: () => void;
}) {
  const placeholders = segments
    .filter((s) => s.kind === 'placeholder')
    .map((s) => (s as { name: string }).name);
  const [configJson, setConfigJson] = useState(JSON.stringify(action.config, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);

  function commitConfig() {
    try {
      const parsed = configJson.trim() === '' ? {} : JSON.parse(configJson);
      onChange({ config: parsed });
      setParseError(null);
    } catch (err) {
      setParseError((err as Error).message);
    }
  }

  return (
    <div className="rounded-md border border-border bg-bg2 p-2.5">
      <div className="mb-2 flex items-center gap-2">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-bg text-[10px] font-semibold text-text2">
          {index + 2}
        </span>
        <select
          value={action.type}
          onChange={(e) => onChange({ type: e.target.value as TelegramActionType })}
          className={`${inputCls} h-7 flex-1 text-xs`}
        >
          {handlers.map((h) => (
            <option key={h.type} value={h.type}>
              {h.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-danger hover:underline"
        >
          ✕ Удалить
        </button>
      </div>
      {placeholders.length > 0 && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1 text-[10px]">
          <span className="text-text-subtle">Доступные переменные:</span>
          {placeholders.map((p) => (
            <span
              key={p}
              className="inline-flex items-center rounded border border-border bg-bg px-1.5 py-0.5 font-mono text-[10px] text-text2"
            >
              {`{${p}}`}
            </span>
          ))}
        </div>
      )}
      <label className={fieldLabelCls}>Параметры (JSON)</label>
      <textarea
        value={configJson}
        onChange={(e) => setConfigJson(e.target.value)}
        onBlur={commitConfig}
        rows={Math.min(6, configJson.split('\n').length + 1)}
        className={`${textareaCls} w-full font-mono text-xs`}
      />
      {parseError && <div className="mt-1 text-[10px] text-danger">JSON: {parseError}</div>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// v2: GuardsEditor — список условий срабатывания правила
// ════════════════════════════════════════════════════════════════════════════

const GUARD_OPS: Array<{ value: string; label: string }> = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
  { value: 'contains', label: 'содержит' },
  { value: 'matches', label: 'regex' },
  { value: 'in', label: 'в списке' },
];

function GuardsEditor({
  guards,
  onChange,
  segments,
}: {
  guards: RuleGuard[];
  onChange: (g: RuleGuard[]) => void;
  segments: Segment[];
}) {
  function addCapture() {
    onChange([...guards, { kind: 'capture', field: '', op: 'eq', value: '' }]);
  }
  function addTime() {
    onChange([...guards, { kind: 'time-range', fromHour: 9, toHour: 18, timezone: 'UTC' }]);
  }
  function addWeekday() {
    onChange([...guards, { kind: 'weekday', days: [1, 2, 3, 4, 5] }]);
  }
  function removeAt(i: number) {
    onChange(guards.filter((_, idx) => idx !== i));
  }
  function updateAt(i: number, patch: Partial<RuleGuard>) {
    onChange(
      guards.map((g, idx) => (idx === i ? ({ ...g, ...patch } as RuleGuard) : g)),
    );
  }

  const placeholders = segments
    .filter((s) => s.kind === 'placeholder')
    .map((s) => (s as { name: string }).name);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-text2">
          Условия срабатывания (все должны выполниться)
        </div>
        <div className="flex gap-2 text-xs">
          <button type="button" onClick={addCapture} className="text-accent hover:underline">
            + Сравнение
          </button>
          <button type="button" onClick={addTime} className="text-accent hover:underline">
            + Время
          </button>
          <button type="button" onClick={addWeekday} className="text-accent hover:underline">
            + День недели
          </button>
        </div>
      </div>
      {guards.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-bg px-3 py-2 text-xs text-text-subtle">
          Без условий правило срабатывает при каждом совпадении regex.
        </div>
      ) : (
        <div className="space-y-1.5">
          {guards.map((g, i) => (
            <GuardCard
              key={i}
              guard={g}
              placeholders={placeholders}
              onChange={(patch) => updateAt(i, patch)}
              onRemove={() => removeAt(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GuardCard({
  guard,
  placeholders,
  onChange,
  onRemove,
}: {
  guard: RuleGuard;
  placeholders: string[];
  onChange: (patch: Partial<RuleGuard>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-bg2 p-2 text-xs">
      {guard.kind === 'capture' && (
        <>
          <span className="text-text-subtle">Если</span>
          <select
            value={guard.field}
            onChange={(e) => onChange({ field: e.target.value })}
            className={`${inputCls} h-7 w-32 px-2 text-xs`}
          >
            <option value="">— переменная —</option>
            {placeholders.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            value={guard.op}
            onChange={(e) => onChange({ op: e.target.value as never })}
            className={`${inputCls} h-7 w-28 px-2 text-xs`}
          >
            {GUARD_OPS.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
          <input
            value={String(guard.value)}
            onChange={(e) => onChange({ value: e.target.value })}
            className={`${inputCls} h-7 flex-1 px-2 text-xs`}
            placeholder="значение"
          />
        </>
      )}
      {guard.kind === 'time-range' && (
        <>
          <span className="text-text-subtle">Время в диапазоне</span>
          <input
            type="number"
            value={guard.fromHour}
            onChange={(e) => onChange({ fromHour: Number(e.target.value) })}
            className={`${inputCls} h-7 w-16 px-2 text-center text-xs`}
            min={0}
            max={23}
          />
          <span className="text-text-subtle">—</span>
          <input
            type="number"
            value={guard.toHour}
            onChange={(e) => onChange({ toHour: Number(e.target.value) })}
            className={`${inputCls} h-7 w-16 px-2 text-center text-xs`}
            min={0}
            max={23}
          />
          <span className="text-text-subtle">час, TZ:</span>
          <input
            value={guard.timezone}
            onChange={(e) => onChange({ timezone: e.target.value })}
            className={`${inputCls} h-7 w-32 px-2 text-xs`}
            placeholder="UTC"
          />
        </>
      )}
      {guard.kind === 'weekday' && (
        <>
          <span className="text-text-subtle">Дни недели:</span>
          {['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'].map((label, idx) => (
            <label key={idx} className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={guard.days.includes(idx)}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...guard.days, idx]
                    : guard.days.filter((d) => d !== idx);
                  onChange({ days: next.sort() });
                }}
              />
              <span className="text-text">{label}</span>
            </label>
          ))}
        </>
      )}
      <button type="button" onClick={onRemove} className="ml-auto text-danger hover:underline">
        ✕
      </button>
    </div>
  );
}

